import React, { useRef, useCallback, useMemo, useState } from 'react'
import _range from 'lodash/range'
import _uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import { FormBlockWrapper, SelectInputWrapper, RequiredText, ButtonWrapper } from './style'
import { TimePickerWrapper } from 'components/TimePickerDiffTime/style'
import TimePickerDiffTime from 'components/TimePickerDiffTime'
import SelectOptions, { DEFAULT_OTHER_OPTION_SELECT } from 'components/SelectOptions'
import SingleForm from 'components/SingleForm'
import theme from 'vars/theme'
import { Label, Input } from 'reactstrap'
import styled from 'styled-components'
import CheckboxSp from './CheckboxSp.js'
import { border } from 'polished'

interface TimerWithDriveProps {
  data?: any
  skipInstructionTime?: boolean
  handleChange?: (value: string | boolean, fieldName: string) => void
  isEntryInstructionTimeRequired: boolean
  isShowNoteSetTimerWithDrive: boolean
  onChangeDriverSetting?: (fieldName: string, value: string | boolean) => void
  handleClearDriverSetting: () => void
  errors?: any
  driverDateTime?: string
  driverCombinedTime?: string
  isValidateDriverSetting: {
    driverDate?: boolean
    driverHours?: boolean
    driverMinutes?: boolean
    driverDateTime?: boolean
    driverCombinedTime?: boolean
  },
  isSp?: boolean,
}

interface DriverInfo {
  driverDate: string
  driverHours: string
  driverMinutes: string
}

const STANDARD_DAY_FORMAT = 'YYYY-MM-DD'
const STANDARD_FULL_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm'

const roundUpDateMoment = () => {
  const momentTime = moment(moment().utcOffset('+09:00').format(STANDARD_FULL_DATE_TIME_FORMAT))
  const minutes = momentTime.minute()
  momentTime.add((5 - (minutes % 5)) % 5, 'minutes')
  momentTime.seconds(0).milliseconds(0)
  return momentTime
}

const formatTimeUnit = (value: string, unit: string) => {
  const units: { [key: string]: string } = {
    HH: '時',
    mm: '分',
  }
  return `${value}${units[unit]}`
}

const findAndScrollToElement = (format: string, driverInfo: DriverInfo) => {
  const roundedDate = roundUpDateMoment()
  const value = roundedDate.format(format)
  const { driverDate, driverHours, driverMinutes } = driverInfo || {}

  setTimeout(() => {
    const today =
      driverDate && driverHours && driverMinutes
        ? `${driverDate} ${driverHours}:${driverMinutes}`
        : format === STANDARD_DAY_FORMAT
          ? roundedDate.format(STANDARD_DAY_FORMAT)
          : formatTimeUnit(value, format)

    // ページのスクロール位置を記録
    const currentScrollY = window.pageYOffset

    const matchingItem = document.querySelectorAll('.mono-select__option')
    const el = Array.from(matchingItem).find((el) => el.textContent === today)

    if (el) {
      // メニューリスト内でのスクロールのみを実行
      const menuList = el.closest('.mono-select__menu-list') as HTMLElement
      if (menuList) {
        const elementRect = el.getBoundingClientRect()
        const menuRect = menuList.getBoundingClientRect()
        const relativeTop = elementRect.top - menuRect.top + menuList.scrollTop
        const menuCenter = menuList.clientHeight / 2
        const elementCenter = el.clientHeight / 2

        menuList.scrollTo({
          top: relativeTop - menuCenter + elementCenter,
          behavior: 'auto'
        })

        // ページのスクロール位置を元に戻す
        window.scrollTo(0, currentScrollY)
      }
    }
  }, 0)
}

// Generate options for time in 15-minute intervals
const generateTimeOptions = () =>
  _range(0, 24).flatMap((hour) => {
    const hourStr = hour.toString().padStart(2, '0')
    return _range(0, 60, 5).map((minute) => ({
      label: `${hourStr}:${minute.toString().padStart(2, '0')}`,
      value: `${hourStr}:${minute.toString().padStart(2, '0')}`,
    }))
  })

// Generate options for dates(-1 to + 1 days)
const generateDateOptions = () => {
  const today = moment()
  return Array.from({ length: 3 }, (_, i) => {
    const targetDate = today.clone().subtract(1 - i, 'days')
    const formattedDate = targetDate.format('YYYY-MM-DD')
    return {
      value: formattedDate,
      label: formattedDate,
    }
  })
}

const defaultUnassignOptions = {
  value: DEFAULT_OTHER_OPTION_SELECT,
  label: '未定',
}


const RequiredTextSp = styled.span`
  vertical-align: middle;
  font-size: 10px;
  padding: 2px 3px;
  border-radius: 2px;
  color: white;
  background-color: ${theme.orangeMedium6};
  margin: 0 4px 0 0;
`

const TimerWithDriver: React.FC<TimerWithDriveProps> = ({
  data,
  skipInstructionTime,
  isShowNoteSetTimerWithDrive,
  isEntryInstructionTimeRequired,
  errors,
  driverDateTime,
  driverCombinedTime,
  isValidateDriverSetting,
  handleClearDriverSetting,
  handleChange,
  onChangeDriverSetting,
  isSp = false,
}) => {
  const driverDateRef = useRef(data?.driverDate || DEFAULT_OTHER_OPTION_SELECT)
  // Update form data and validate field
  const handleUpdateFormData = useCallback(
    (fieldName: string, value: string | boolean) => {
      if (isShowNoteSetTimerWithDrive) {
        onChangeDriverSetting?.(fieldName, value)
      }
      handleChange?.(value, fieldName)
    },
    [handleChange, isShowNoteSetTimerWithDrive, onChangeDriverSetting]
  )
  const timeOptions = useMemo(generateTimeOptions, [])
  const dateDefaultSelectOptions = useMemo(generateDateOptions, [])
  const dateOptions = useMemo(() => {
    const formattedDates = data?.bookingNo
      ? [
        ...dateDefaultSelectOptions,
        {
          label: driverDateRef.current,
          value: driverDateRef.current,
        },
      ]
      : dateDefaultSelectOptions
    return _uniqBy(formattedDates, 'value').sort((a, b) => moment(a.value).diff(moment(b.value)))
  }, [data, dateDefaultSelectOptions])
  const combinedTimeOptions = useMemo(() => _uniqBy([...timeOptions], 'value'), [timeOptions])
  const { driverDate, driverHours, driverMinutes } = data || {}
  const newDataDates = [defaultUnassignOptions, ...dateOptions].filter(
    (item) => item.label !== DEFAULT_OTHER_OPTION_SELECT
  )
  const formConfig = useMemo(
    () => ({
      formWidth: 360,
      options: {
        dates: newDataDates,
        hours: [
          defaultUnassignOptions,
          ..._range(0, 24).map((hour) => ({
            label: `${hour.toString().padStart(2, '0')}時`,
            value: hour.toString().padStart(2, '0'),
          })),
        ],
        minutes: [
          defaultUnassignOptions,
          ..._range(0, 60, 5).map((minute) => ({
            label: `${minute.toString().padStart(2, '0')}分`,
            value: minute.toString().padStart(2, '0'),
          })),
        ],
        combinedTimes: [defaultUnassignOptions, ...combinedTimeOptions],
      },
    }),
    [newDataDates, combinedTimeOptions]
  )

  if (isSp) {
    return (
      <FormBlockWrapper
        style={{ borderBottom: '0px solid' }}
      >
        <div
          className="d-flex mb-3 flex-shrink-0 align-items-center"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {isShowNoteSetTimerWithDrive ? (
            <h1
              className="font-weight-bold"
              style={{
                fontSize: '20px',
                color: 'black',
              }}
            >入場指示時刻</h1>
          ) : (
            <h3>入場指示時刻</h3>
          )}
          <div>
            <ButtonWrapper
              outline
              color="primary"
              className="bg-white ml-2 px-4 text-primary"
              size="md"
              height="40px"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => handleClearDriverSetting()}
            >
              クリア
            </ButtonWrapper>
          </div>
        </div>
        <div style={{ fontSize: '14px' }}>
          <p className="mb-0">入場時刻を指定されている場合は入力してください。</p>
          <p>特に指定されていない場合は「指示を受けていない」にチェックを入れてください。</p>
        </div>

        <div className="">

          <CheckboxSp
            id="skip_instruction_time"
            label="指示を受けていない"
            className="mb-4"
            checked={skipInstructionTime}
            onChange={(val: boolean) => {
              handleUpdateFormData(isShowNoteSetTimerWithDrive ? 'skipInstructionTime' : 'skip_instruction_time', val)
              handleClearDriverSetting()
            }}
            isSp
          />
        </div>

        <div className="">
          {isShowNoteSetTimerWithDrive ? (
            <>
              <div className="">
                <div >
                  <div className="my-2" style={{ fontSize: '16px' }}>
                    {isEntryInstructionTimeRequired && <RequiredTextSp>必須</RequiredTextSp>}
                    <span>日程</span>
                  </div>
                  <SelectOptions
                    maxWidth='100%'
                    isDisabled={skipInstructionTime}
                    className="w-100 bg-white"
                    name="driverDate"
                    options={formConfig.options.dates}
                    selectValue={driverDate || DEFAULT_OTHER_OPTION_SELECT}
                    handleUpdateData={(value: any) => handleUpdateFormData('driverDate', value)}
                    skipAutoUpdate
                    isError={isValidateDriverSetting?.driverDate}
                    styles={{
                      control: (provided: any) => ({
                        ...provided,
                        height: '60px',
                        minHeight: '60px',
                      }),
                      option: (provided: any, { label }: { label: string }) => {
                        const isDefaultDate =
                          driverDate === DEFAULT_OTHER_OPTION_SELECT || !driverDate
                        return isDefaultDate &&
                          label === roundUpDateMoment().format(STANDARD_DAY_FORMAT)
                          ? {
                            ...provided,
                            color: 'black',
                            fontWeight: 'bold',
                          }
                          : provided
                      },
                    }}
                    onMenuOpen={() => findAndScrollToElement(STANDARD_DAY_FORMAT, data)}
                  />
                </div>
                <div>
                  <div className="my-2" style={{ fontSize: '16px' }}>
                    {isEntryInstructionTimeRequired && <RequiredTextSp>必須</RequiredTextSp>}
                    <span>時刻</span>
                  </div>
                  <div className="d-flex align-items-center mb-1 mt-1">
                    <TimePickerWrapper>
                      <TimePickerDiffTime
                        width='100%'
                        isDisabled={skipInstructionTime}
                        name="driverHours"
                        options={formConfig.options.hours}
                        selectValue={driverHours}
                        onChange={(value) => handleUpdateFormData('driverHours', value)}
                        hasClass={false}
                        isError={isValidateDriverSetting?.driverHours}
                        displayValue={
                          driverHours === DEFAULT_OTHER_OPTION_SELECT || !driverHours
                            ? formatTimeUnit(roundUpDateMoment().format('HH'), 'HH')
                            : formatTimeUnit(driverHours, 'HH')
                        }
                        onMenuOpen={() => findAndScrollToElement('HH', data)}
                      />
                    </TimePickerWrapper>
                    <TimePickerWrapper>
                      <TimePickerDiffTime
                        isDisabled={skipInstructionTime}
                        name="driverMinutes"
                        options={formConfig.options.minutes}
                        selectValue={driverMinutes}
                        onChange={(value) => handleUpdateFormData('driverMinutes', value)}
                        hasClass={false}
                        isError={isValidateDriverSetting?.driverMinutes}
                        displayValue={
                          driverMinutes === DEFAULT_OTHER_OPTION_SELECT || !driverMinutes
                            ? formatTimeUnit(roundUpDateMoment().format('mm'), 'mm')
                            : formatTimeUnit(driverMinutes, 'mm')
                        }
                        onMenuOpen={() => findAndScrollToElement('mm', data)}
                      />
                    </TimePickerWrapper>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <SingleForm
                id="driverDateTime"
                name="driverDateTime"
                label="指示日"
                isDisabled={skipInstructionTime}
                value={driverDateTime}
                onChange={(value: string) =>
                  handleUpdateFormData('driverDateTime', moment(value).format('YYYY-MM-DD'))
                }
                isDayPicker
                errorMessage={isValidateDriverSetting?.driverDateTime}
              />
              <div className="d-flex align-items-center mb-1 mt-1">
                <span className="label-flex" style={{ whiteSpace: 'nowrap' }}>
                  指示時間
                </span>
                <TimePickerDiffTime
                  isDisabled={skipInstructionTime}
                  name="driverCombinedTime"
                  options={formConfig.options.combinedTimes}
                  selectValue={driverCombinedTime}
                  onChange={(value) => handleUpdateFormData('driverCombinedTime', value)}
                  hasClass={false}
                  isError={isValidateDriverSetting?.driverCombinedTime}
                />
              </div>
            </>
          )}
        </div>

        <div className="d-flex">
          <div style={{ width: isShowNoteSetTimerWithDrive ? 10 : 140 }} className="label" />
          <div className="d-flex align-items-center mb-1 mt-1">
            {errors && (
              <p
                className="text-left ml-1"
                style={{ color: `${theme.redMedium1}`, fontSize: '14px' }}
              >
                {errors.commonError}
              </p>
            )}
          </div>
        </div>
      </FormBlockWrapper >
    )
  }
  return (
    <FormBlockWrapper>
      <div
        className="d-flex mb-3 flex-shrink-0 align-items-center"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: isShowNoteSetTimerWithDrive
            ? isEntryInstructionTimeRequired
              ? '490px'
              : '430px'
            : '100%',
        }}
      >
        {isShowNoteSetTimerWithDrive ? (
          <>
            <h1 className="text-primary">入場指示時刻</h1>
          </>
        ) : (
          <>
            <h3>入場指示時刻</h3>
          </>
        )}
        <div>
          <ButtonWrapper
            outline
            color="primary"
            className="bg-white ml-2 px-md-4 px-sm-1 px-0 text-primary"
            size="sm"
            height="40px"
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => handleClearDriverSetting()}
          >
            クリア
          </ButtonWrapper>
        </div>
      </div>

      <div className="input-flex">
        <SingleForm
          className="input-flex"
          id="skip_instruction_time"
          name="skip_instruction_time"
          type="checkbox"
          label="指示を受けていない"
          value={skipInstructionTime}
          onChange={(val: boolean) => {
            handleUpdateFormData(isShowNoteSetTimerWithDrive ? 'skipInstructionTime' : 'skip_instruction_time', val)
            handleClearDriverSetting()
          }}
          isCheckType
          isHightLight
        />
      </div>

      <div className="d-flex">
        {isShowNoteSetTimerWithDrive ? (
          <>
            <SelectInputWrapper>
              {isEntryInstructionTimeRequired && <RequiredText>{<>必須</>}</RequiredText>}
              <div style={{ width: 95 }} className="label">
                指示日時
              </div>
              <div className="d-flex">
                <div style={{ width: 140 }}>
                  <SelectOptions
                    isDisabled={skipInstructionTime}
                    className="w-100 bg-white"
                    name="driverDate"
                    options={formConfig.options.dates}
                    selectValue={driverDate || DEFAULT_OTHER_OPTION_SELECT}
                    handleUpdateData={(value: any) => handleUpdateFormData('driverDate', value)}
                    skipAutoUpdate
                    isError={isValidateDriverSetting?.driverDate}
                    styles={{
                      control: (provided: any) => ({
                        ...provided,
                        height: '60px',
                        minHeight: '60px',
                      }),
                      option: (provided: any, { label }: { label: string }) => {
                        const isDefaultDate =
                          driverDate === DEFAULT_OTHER_OPTION_SELECT || !driverDate
                        return isDefaultDate &&
                          label === roundUpDateMoment().format(STANDARD_DAY_FORMAT)
                          ? {
                            ...provided,
                            color: 'black',
                            fontWeight: 'bold',
                          }
                          : provided
                      },
                    }}
                    onMenuOpen={() => findAndScrollToElement(STANDARD_DAY_FORMAT, data)}
                  />
                </div>
                <div style={{ width: 190, marginLeft: '4px' }}>
                  <div className="d-flex">
                    <TimePickerWrapper>
                      <TimePickerDiffTime
                        isDisabled={skipInstructionTime}
                        name="driverHours"
                        options={formConfig.options.hours}
                        selectValue={driverHours}
                        onChange={(value) => handleUpdateFormData('driverHours', value)}
                        hasClass={false}
                        isError={isValidateDriverSetting?.driverHours}
                        displayValue={
                          driverHours === DEFAULT_OTHER_OPTION_SELECT || !driverHours
                            ? formatTimeUnit(roundUpDateMoment().format('HH'), 'HH')
                            : formatTimeUnit(driverHours, 'HH')
                        }
                        onMenuOpen={() => findAndScrollToElement('HH', data)}
                      />
                    </TimePickerWrapper>
                    <TimePickerWrapper>
                      <TimePickerDiffTime
                        isDisabled={skipInstructionTime}
                        name="driverMinutes"
                        options={formConfig.options.minutes}
                        selectValue={driverMinutes}
                        onChange={(value) => handleUpdateFormData('driverMinutes', value)}
                        hasClass={false}
                        isError={isValidateDriverSetting?.driverMinutes}
                        displayValue={
                          driverMinutes === DEFAULT_OTHER_OPTION_SELECT || !driverMinutes
                            ? formatTimeUnit(roundUpDateMoment().format('mm'), 'mm')
                            : formatTimeUnit(driverMinutes, 'mm')
                        }
                        onMenuOpen={() => findAndScrollToElement('mm', data)}
                      />
                    </TimePickerWrapper>
                  </div>
                </div>
              </div>
              <div style={{ marginLeft: '12px' }}>
                <div className="flex-1" style={{ whiteSpace: 'nowrap' }}>
                  <p className="font-md mb-0">
                    入場時刻を指定されている場合は入力してください。
                    <br />
                    特に指定されていない場合は「指示を受けていない」にチェックを入れてください。
                  </p>
                </div>
              </div>
            </SelectInputWrapper>
          </>
        ) : (
          <>
            <SingleForm
              id="driverDateTime"
              name="driverDateTime"
              label="指示日"
              isDisabled={skipInstructionTime}
              value={driverDateTime}
              onChange={(value: string) =>
                handleUpdateFormData('driverDateTime', moment(value).format('YYYY-MM-DD'))
              }
              isDayPicker
              errorMessage={isValidateDriverSetting?.driverDateTime}
            />
          </>
        )}
      </div>

      <div className="d-flex align-items-center mb-1 mt-1">
        {!isShowNoteSetTimerWithDrive && (
          <>
            <span className="label-flex" style={{ whiteSpace: 'nowrap' }}>
              指示時間
            </span>
            <TimePickerDiffTime
              isDisabled={skipInstructionTime}
              name="driverCombinedTime"
              options={formConfig.options.combinedTimes}
              selectValue={driverCombinedTime}
              onChange={(value) => handleUpdateFormData('driverCombinedTime', value)}
              hasClass={false}
              isError={isValidateDriverSetting?.driverCombinedTime}
            />
          </>
        )}
      </div>

      <div className="d-flex">
        <div style={{ width: isShowNoteSetTimerWithDrive ? 95 : 140 }} className="label" />
        <div className="d-flex align-items-center mb-1 mt-1">
          {errors && (
            <p
              className="text-left ml-1"
              style={{ color: `${theme.redMedium1}`, fontSize: '12px' }}
            >
              {errors.commonError}
            </p>
          )}
        </div>
      </div>
    </FormBlockWrapper>
  )
}

export default TimerWithDriver
